import { Button } from "@chakra-ui/button";
import { useDisclosure } from "@chakra-ui/hooks";
import { Box, Flex, Text } from "@chakra-ui/layout";
import {
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/popover";
import { Portal } from "@chakra-ui/react";
import React from "react";
import { SalesforceApi } from "../../api/SalesforceApi";
import useUser from "../../hooks/useUser";
import { SFDeal } from "../../lib/models/sf-deal.model";
import { SelectItem } from "../../lib/types/SelectItem";
import ComboBox from "../form-fields/ComboBox/index";
import { BriefcaseIcon } from "../icons/BriefcaseIcon";
import { BuildingIcon } from "../icons/BuildingIcon";
import ShowForRole from "../ShowForRole";
import WarningDialog from "../WarningDialog";

const findAccountById = async (
  accountid: string
): Promise<SelectItem | null> => {
  const account = (await SalesforceApi.getAccountById(accountid)).data;

  if (!!Object.keys(account).length) {
    return {
      label: account.name,
      value: account.accountid,
    };
  } else {
    return null;
  }
};

const searchAccountsByQuery = async (
  query: string
): Promise<Array<SelectItem>> => {
  const data = await SalesforceApi.getAccountsByQuery(query);
  return data.data.map((account) => ({
    label: account.name,
    value: account.accountid,
  }));
};

export interface FormSfAccountDealSelectorProps {
  onAccountDealUpdate: (
    accountId: string,
    dealId: string,
    campaignId: string,
    campaignName: string,
    campaignExpirationDate: string,
    campaignIncentive: string,
    campaignCode: string
  ) => void;
  accountId: string | undefined;
  dealId: string | undefined;
  hasSubmitted: boolean;
  isSubmitting: boolean;
  campaignId: string | undefined;
}

function FormSfAccountDealSelector(props: FormSfAccountDealSelectorProps) {
  const userContext = useUser();
  const {
    onAccountDealUpdate,
    accountId,
    dealId,
    hasSubmitted,
    isSubmitting,
    campaignId,
  } = props;
  const [dealsLoading, setDealsLoading] = React.useState<boolean>(false);
  const [hasSelected, setHasSelected] = React.useState<boolean>(false);
  const _isMounted = React.useRef(true);

  const {
    isOpen: isAccountPopoverOpen,
    onClose: onAccountPopoverClose,
    onOpen: onAccountPopoverOpen,
  } = useDisclosure();

  const {
    isOpen: isDealPopoverOpen,
    onClose: onDealPopoverClose,
    onOpen: onDealPopoverOpen,
  } = useDisclosure();

  const [isChangedSfDialogOpen, setIsChangedSfDialogOpen] =
    React.useState(false);

  const [hasDialogBeenShown, setHasDialogBeenShown] = React.useState(false);

  const [sfDeals, setSfDeals] = React.useState<SFDeal[]>([]);

  const sfAccountsItems: SelectItem[] = React.useMemo(() => {
    return userContext.sfAccounts.map((account) => ({
      label: account.name,
      value: account.accountid,
    }));
  }, [userContext.sfAccounts]);

  const sfDealsItems: SelectItem[] = React.useMemo(() => {
    return sfDeals.map((deal) => ({
      label: deal.dealname,
      value: deal.dealid,
    }));
  }, [sfDeals]);

  const sfCampaignItems: SelectItem[] = React.useMemo(() => {
    return sfDeals.map((deal) => ({
      label: deal.campaignName,
      value: deal.campaignId,
    }));
  }, [sfDeals]);

  const [selectedSfAccount, setSelectedSfAccount] =
    React.useState<SelectItem | null>(
      sfAccountsItems.find((item) => item.value === accountId) ?? null
    );

  const [selectedSfDeal, setSelectedSfDeal] = React.useState<SelectItem | null>(
    sfDealsItems.find((item) => item.value === dealId) ?? null
  );

  const [selectedSfCampaign, setSelectedSfCampaign] =
    React.useState<SelectItem | null>(
      sfCampaignItems.find((item) => item.value === campaignId) ?? null
    );

  const fetchDeals = React.useCallback(async (accountId: any) => {
    const res = await SalesforceApi.getDeals(accountId);
    const deals = res.data;

    if (_isMounted.current) {
      setSfDeals(deals);
      setDealsLoading(false);
    }
  }, []);

  const handleAccountSelect = (account: SelectItem | null) => {
    setHasSelected(true);
    setSelectedSfAccount(account);
    setSelectedSfDeal(null);
    setSelectedSfCampaign(null);
    onAccountPopoverClose();
  };

  const handleDealSelect = (deal: SelectItem | null) => {
    setHasSelected(true);
    setSelectedSfDeal(deal);
    setSelectedSfCampaign(
      sfCampaignItems.find(
        (item) =>
          item.value ===
          sfDeals.find((sfDeal) => sfDeal.dealid === deal?.value)?.campaignId
      ) ?? null
    );
    onDealPopoverClose();
  };

  const handleWarningDialogClose = () => {
    const account = sfAccountsItems.find((x) => x.value === accountId);
    const deal = sfDealsItems.find((x) => x.value === dealId);
    const campaign =
      sfCampaignItems.find(
        (item) =>
          item.value ===
          sfDeals.find((sfDeal) => sfDeal.dealid === deal?.value)?.campaignId
      ) ?? null;

    if (account && deal) {
      setSelectedSfAccount(account);
      setSelectedSfDeal(deal);
      setSelectedSfCampaign(campaign);
      setIsChangedSfDialogOpen(false);
    }
  };

  const handleWarningDialogConfirm = () => {
    const selectedSfDealValues = sfDeals.find(
      (item) => item.dealid === selectedSfDeal?.value
    );

    onAccountDealUpdate(
      selectedSfAccount?.value as string,
      selectedSfDeal?.value as string,
      selectedSfCampaign?.value as string,
      selectedSfDealValues?.campaignName as string,
      selectedSfDealValues?.campaignExpirationDate as string,
      selectedSfDealValues?.campaignIncentive as string,
      selectedSfDealValues?.campaignCode as string
    );
    setIsChangedSfDialogOpen(false);
  };

  const haveSfValuesChanged = React.useCallback(
    (
      incomingAccountId: string,
      incomingDealId: string,
      incomingCampaignId: string
    ) => {
      if (!hasSubmitted) return false;

      const origAccountId = accountId;
      const origDealId = dealId;
      const origCampaignId = campaignId;

      if (origCampaignId !== incomingCampaignId) {
        return origCampaignId !== incomingCampaignId;
      }

      if (origAccountId && origDealId) {
        return (
          origAccountId !== incomingAccountId || origDealId !== incomingDealId
        );
      }

      return false;
    },
    [accountId, dealId, campaignId, hasSubmitted]
  );

  React.useEffect(() => {
    if (!hasSelected || isSubmitting) return;

    const selectedDeal = selectedSfDeal?.value as string;
    const selectedAccount = selectedSfAccount?.value as string;
    const selectedCampaign = selectedSfCampaign?.value as string;
    const selectedSfDealValues = sfDeals.find(
      (item) => item.dealid === selectedSfDeal?.value
    );

    if (
      !hasDialogBeenShown &&
      haveSfValuesChanged(selectedAccount, selectedDeal, selectedCampaign)
    ) {
      setIsChangedSfDialogOpen(true);
      setHasDialogBeenShown(true);
    } else {
      onAccountDealUpdate(
        selectedAccount,
        selectedDeal,
        selectedCampaign,
        selectedSfDealValues?.campaignName as string,
        selectedSfDealValues?.campaignExpirationDate as string,
        selectedSfDealValues?.campaignIncentive as string,
        selectedSfDealValues?.campaignCode as string
      );
    }
  }, [
    hasSelected,
    selectedSfAccount,
    selectedSfDeal,
    selectedSfCampaign,
    onAccountDealUpdate,
    haveSfValuesChanged,
    hasDialogBeenShown,
    isSubmitting,
    sfDeals,
  ]);

  React.useEffect(() => {
    if (selectedSfAccount) {
      setDealsLoading(true);
      fetchDeals(selectedSfAccount.value).catch((e) => {
        setDealsLoading(false);
      });
    }
  }, [selectedSfAccount, fetchDeals]);

  React.useEffect(() => {
    return () => {
      _isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!accountId) return;

    const accountFromCache = sfAccountsItems.find((x) => x.value === accountId);

    if (accountFromCache) {
      setSelectedSfAccount(accountFromCache);
      return;
    }

    findAccountById(accountId).then((account) => {
      account && setSelectedSfAccount(account);
    });
  }, [accountId, sfAccountsItems]);

  React.useEffect(() => {
    if (accountId) {
      setSelectedSfDeal(sfDealsItems.find((x) => x.value === dealId) ?? null);
    }
  }, [sfDealsItems, dealId, accountId]);

  React.useEffect(() => {
    if (dealId && sfCampaignItems && sfDeals) {
      setSelectedSfCampaign(
        sfCampaignItems.find(
          (item) =>
            item.value ===
            sfDeals.find((sfDeal) => sfDeal.dealid === dealId)?.campaignId
        ) ?? null
      );
    }
  }, [setSelectedSfCampaign, dealId, sfCampaignItems, sfDeals]);

  return (
    <>
      <Box marginBottom={2}>
        <Flex alignItems="center" color="gray.500" fontWeight="light">
          <BuildingIcon mr={2} />
          <Text fontSize="xs">Account Name</Text>
        </Flex>

        <Popover
          isOpen={isAccountPopoverOpen}
          onOpen={onAccountPopoverOpen}
          onClose={onAccountPopoverClose}
          placement="auto-start"
          size="md"
          z-Index={"popover"}
        >
          <PopoverTrigger>
            <Button
              fontWeight="medium"
              fontSize="sm"
              textAlign="left"
              variant="link"
              marginTop="5px"
            >
              <Text
                maxW={{ base: "250px", lg: "200px" }}
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                title={
                  selectedSfAccount
                    ? selectedSfAccount.label
                    : "Select to link an account"
                }
              >
                {selectedSfAccount
                  ? selectedSfAccount.label
                  : "Select to link an account"}
              </Text>
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent p={5}>
              <PopoverArrow />
              <PopoverCloseButton />
              <ShowForRole
                roles={["Production Coordinator", "Operations Analyst"]}
              >
                <ComboBox
                  items={sfAccountsItems}
                  label="Account Name"
                  onSelect={handleAccountSelect}
                  itemSelected={selectedSfAccount}
                  onSearch={searchAccountsByQuery}
                  isDisabled={!!selectedSfAccount}
                />
              </ShowForRole>
              <ShowForRole
                roles={[
                  "External",
                  "Sales",
                  "Broker",
                  "Broker Manager",
                  "Lender Finance",
                  "Lender Finance Manager",
                  "Wholesale Manager",
                  "Inside Sales",
                  "Inside Sales Manager",
                  "Business Development",
                  "Capital Markets",
                  "Business Development Manager",
                  "POD",
                  "POD Manager",
                  "Sales Assistant",
                ]}
                hideForAdmin
              >
                <ComboBox
                  items={sfAccountsItems}
                  label="Account Name"
                  onSelect={handleAccountSelect}
                  itemSelected={selectedSfAccount}
                  isDisabled={!!selectedSfAccount}
                />
              </ShowForRole>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>

      <Box marginBottom={2}>
        <Flex alignItems="center" color="gray.500" fontWeight="light">
          <BriefcaseIcon mr={2} />
          <Text fontSize="xs">Deal Name</Text>
        </Flex>

        {dealsLoading ? (
          <Button
            fontWeight="medium"
            fontSize="sm"
            textAlign="left"
            variant="link"
            marginTop="5px"
            isLoading
            loadingText="loading deals.."
          ></Button>
        ) : (
          <Popover
            isOpen={isDealPopoverOpen}
            onOpen={onDealPopoverOpen}
            onClose={onDealPopoverClose}
            placement="auto-start"
            size="md"
          >
            <PopoverTrigger>
              <Button
                fontWeight="medium"
                fontSize="sm"
                textAlign="left"
                variant="link"
                marginTop="5px"
                isLoading={dealsLoading}
                loadingText="loading deals.."
                isDisabled={!selectedSfAccount}
              >
                <Text
                  maxW={{ base: "250px", lg: "200px" }}
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  title={
                    selectedSfDeal
                      ? selectedSfDeal.label
                      : "Select to link a deal"
                  }
                >
                  {selectedSfDeal
                    ? selectedSfDeal.label
                    : "Select to link a deal"}
                </Text>
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent p={5}>
                <PopoverArrow />
                <PopoverCloseButton />
                <ComboBox
                  items={sfDealsItems}
                  label="Deal Name"
                  onSelect={handleDealSelect}
                  itemSelected={selectedSfDeal}
                  isDisabled={!!selectedSfDeal}
                />
              </PopoverContent>
            </Portal>
          </Popover>
        )}
      </Box>

      {selectedSfCampaign?.label && (
        <Box marginBottom={4}>
          <Flex alignItems="center" color="gray.500" fontWeight="light">
            <BriefcaseIcon mr={2} />
            <Text fontSize="xs">Campaign Name</Text>
          </Flex>

          <Button
            fontWeight="medium"
            fontSize="sm"
            textAlign="left"
            variant="link"
            isDisabled={true}
          >
            <Text
              maxW={{ base: "250px", lg: "200px" }}
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
              title={selectedSfCampaign?.label ?? undefined}
            >
              {selectedSfCampaign?.label ?? undefined}
            </Text>
          </Button>
        </Box>
      )}

      <WarningDialog
        isOpen={isChangedSfDialogOpen}
        onConfirmHandler={handleWarningDialogConfirm}
        onCloseHandler={handleWarningDialogClose}
        titleText="Are you sure?"
        contentText="You have selected a new account and/or deal on a quote that had been previously submitted. Changing this will require a quote resubmission. Are you sure you want to continue?"
        confirmText="Yes, continue"
        danger={false}
      />
    </>
  );
}

export default FormSfAccountDealSelector;
